<script lang="ts">
	import { createEventDispatcher, getContext, hasContext } from 'svelte'
	import { writable, type Writable } from 'svelte/store'
	import { SvelteMap } from 'svelte/reactivity'
	import { LotType } from '$types/enums'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'
	import type { WhitelistSelectorData } from './whitelistData'
	import StaticChipList from '$lib/organisms/StaticChipList.svelte'
	import { t } from '$i18n/i18n'

	const transportConfigData: Writable<Map<LotType, WhitelistSelectorData[]>> = hasContext(
		'whitelistTransportConfigData'
	)
		? getContext('whitelistTransportConfigData')
		: writable(new Map<LotType, WhitelistSelectorData[]>())
	const selectedTransportData: Writable<Map<LotType, Map<string, string[]>>> = hasContext(
		'selectedWhitelistTransportData'
	)
		? getContext('selectedWhitelistTransportData')
		: writable(new Map<LotType, Map<string, string[]>>())
	const selectedLotType: Writable<LotType> = hasContext('currentLotType')
		? getContext('currentLotType')
		: writable(LotType.TRANSPORT)
	const headerFontProps: FontProps = DefaultRobotoFontProps(
		'1rem',
		'0.75rem',
		undefined,
		undefined,
		500
	)
	const dispatch = createEventDispatcher()

	function getChipListEntries(entries: string[]) {
		return entries.map((entry) => ({
			label: $t('UI.whitelist.transport.table.' + entry),
			value: entry,
			msg: ''
		}))
	}

	function getSelectorSelection(selector: WhitelistSelectorData): string[] {
		if (!$selectedLotType) return []
		return $selectedTransportData.get($selectedLotType)?.get(selector.nodeSelector) ?? []
	}

	// setter function;
	function getUpdatedSelectionForAdd(
		selector: WhitelistSelectorData,
		selectedValue: string
	): string[] {
		// FIXME: DK - maybe pass a "exclusive" flag from the backend; hardcoding against a value is bad
		if (selector.nodeSelector === 'highlighted') {
			return [selectedValue]
		}
		return [...getSelectorSelection(selector), selectedValue]
	}

	function updateSelectorSelection(selector: WhitelistSelectorData, activeChips: string[]) {
		if (!$selectedLotType) return
		let lotTypeData =
			$selectedTransportData.get($selectedLotType) ?? new SvelteMap<string, string[]>()

		// Keep transport selection state aligned with material deselection behavior:
		// remove empty selector entries instead of persisting empty arrays.
		if (activeChips.length === 0) {
			lotTypeData.delete(selector.nodeSelector)
		} else {
			lotTypeData.set(selector.nodeSelector, activeChips)
		}

		// If no selector has active values for this lot type, drop the lot type entry entirely.
		if (lotTypeData.size === 0) {
			$selectedTransportData.delete($selectedLotType)
		} else {
			$selectedTransportData.set($selectedLotType, lotTypeData)
		}

		$selectedTransportData = new Map($selectedTransportData)
		dispatch('transportSelectionChange')
	}
</script>

<div class="whitelist-view-transport">
	{#each $transportConfigData as [lotType, lotSelectors] (lotType)}
		<section class="transport-lot-type">
			<h3 class="lot-type-title">
				{$t('UI.whitelist.transport.table.header')}
			</h3>
			<dl class="selector-list">
				{#each lotSelectors as selector (selector.label)}
					<dt class="selector-entry">
						<fieldset class="selector-fieldset">
							<legend class="selector-label"
								>{selector.label}{#if selector.nodeSelector === 'highlighted'}<span
										class="selector-node__highlighted">*</span
									>{/if}</legend
							>
							<StaticChipList
								id={`${selector.label}-chiplist`}
								identifier={selector}
								header=""
								fontProps={headerFontProps}
								list={getChipListEntries(selector.entries)}
								activeChips={getSelectorSelection(selector)}
								on:addItem={(e) =>
									updateSelectorSelection(
										selector,
										getUpdatedSelectionForAdd(selector, e.detail.selectedItem.value)
									)}
								on:removeItem={(e) =>
									updateSelectorSelection(
										selector,
										getSelectorSelection(selector).filter(
											(value) => value !== e.detail.selectedItem.value
										)
									)}
							/>
						</fieldset>
					</dt>
				{/each}
			</dl>
		</section>
	{/each}
</div>

<style lang="scss">
	@use '$styles/global';

	.whitelist-view-transport {
		width: 100%;
		height: 21.75rem;
		overflow-y: auto;
		padding: 0.5rem 0.875rem 0.75rem;
		text-align: left;
	}

	.lot-type-title {
		@include global.roboto-font(18px, 500, 0.875rem, #000);

		margin: 0 0 0.75rem;
	}

	.selector-list {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.selector-entry {
		margin: 0 0 0.75rem;
	}

	.selector-fieldset {
		padding: 0.75rem 0;
		background: white;
	}

	.selector-label {
		@include global.roboto-font(18px, 500, 0.75rem, #000);

		padding: 0 0.25rem;
	}
</style>
